import React from 'react'
import PropTypes from 'prop-types'
import ProgressIndicator from './ProgressIndicator'

/**
 * MultiStepFlowHeader
 *
 * Header component for multi-step flows with back button, centered logo, and progress indicator.
 *
 * @param {Object} props
 * @param {Function} props.onBack - Callback when back button is clicked
 * @param {boolean} props.showBackButton - Whether to show the back button (default: true)
 * @param {boolean} props.alwaysShowBackButton - If true, shows back button even on first step (default: false)
 * @param {string|React.ReactNode} props.logo - Logo (URL string or React element)
 * @param {number} props.currentStep - Current step index (0-based)
 * @param {number} props.totalSteps - Total number of steps
 * @param {boolean} props.showProgress - Whether to show progress indicator (default: true)
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.stepProgressModifier - Additional modifier to apply to the progress bar (e.g., to show progress one step ahead)
 */
const MultiStepFlowHeader = ({
  onBack,
  showBackButton = true,
  alwaysShowBackButton = false,
  logo,
  currentStep,
  totalSteps,
  showProgress = true,
  className = '',
  stepProgressModifier = 0
}) => {
  return (
    <header className={`px-4 py-12 bg-background ${className}`}>
      <div className="container mx-auto max-w-6xl">
        <div className="grid items-center grid-cols-3 gap-4">
          {/* Left: Back Button */}
          <div className="flex-shrink-0 w-24">
            {showBackButton && (alwaysShowBackButton || currentStep > 0) && (
              <button
                type="button"
                onClick={onBack}
                className="flex items-center gap-2 text-brandSecondary hover:text-brandSecondary/80 transition-colors"
                aria-label="Go back"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                <span className="text-sm font-medium">Back</span>
              </button>
            )}
          </div>

          {/* Center: Logo */}
          <div className="flex-shrink-0 justify-self-center">
            {typeof logo === 'string' ? (
              <img src={logo} alt="Logo" className="h-8" />
            ) : (
              logo
            )}
          </div>

          {/* Right: Progress Indicator */}
          <div className="flex-shrink-0 w-[15vw] sm:w-[25vw] md:w-64 flex justify-end justify-self-end">
            {showProgress && (
              <ProgressIndicator
                currentStep={currentStep}
                totalSteps={totalSteps}
                stepProgressModifier={stepProgressModifier}
              />
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

MultiStepFlowHeader.propTypes = {
  onBack: PropTypes.func,
  showBackButton: PropTypes.bool,
  alwaysShowBackButton: PropTypes.bool,
  logo: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  currentStep: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  showProgress: PropTypes.bool,
  className: PropTypes.string,
  stepProgressModifier: PropTypes.number
}

export default MultiStepFlowHeader